/* eslint-disable max-lines-per-function */
import { useMutation, QueryResult } from "@apollo/client";
import css from "classnames";
import { orderBy } from "lodash";
import moment from "moment";
import React from "react";
import { Modal } from "components/haven-design/modal";
import { Button } from "components/haven-design/buttons";
import GetAppIcon from "@material-ui/icons/GetApp";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import {
  FetchInterestDocuments,
  FetchInterestDocumentsVariables,
  RemoveUploadedDocument,
  RemoveUploadedDocumentVariables,
  FetchInterestDocuments_interest_documents as DocumentType,
} from "types/gql-types";
import { DialogActions, DialogContent } from "components/haven-design/modal/dialog";
import { useSnackbar } from "components/haven-design/snackbar";
import { REMOVE_DOCUMENT } from "../query";
import styles from "./documents.module.css";

type UploadedDocument = NonNullable<FetchInterestDocuments["interest"]>["documents"][0];

const SORT_BY_ARGS = [
  ["category", "uploadedAt", "name"],
  ["asc", "desc", "asc"],
];

interface Props {
  query: QueryResult<FetchInterestDocuments, FetchInterestDocumentsVariables>;
  interestID: string;
}

const documentIsRecent = (document:DocumentType):boolean =>
  moment().diff(moment(document.uploadedAt), "minutes") < 1;

const DocumentRow: React.FC<{
  document: DocumentType,
  documents: DocumentType[],
  index: number,
  setRemovingDocument: (input: DocumentType) => void,
}> = ({
  document, index, documents: sorted,
  setRemovingDocument,
}) => {
  const [isRecent, setIsRecent] = React.useState(documentIsRecent(document));
  const previous = sorted[Math.max(0, index - 1)];
  const changed = index === 0 || document.category !== previous.category;

  React.useEffect(() => {
    if (!isRecent) {
      return () => null;
    }

    const interval = setInterval(() => {
      setIsRecent(documentIsRecent(document));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  return (
    <tr className={css({ [styles.divider]: changed })}>
      <td>
        {changed && <span className="label label-primary">{document.category}</span>}
      </td>
      <td>
        {(document.isPreviewable && !isRecent) ? (
          <a target="_blank" href={document.url} title="Preview" rel="noreferrer">
            {document.name}
          </a>
        ) : (
          <span>
            {document.name}
            {isRecent ? " (processing...)" : ""}
          </span>
        )}
      </td>
      <td>{moment(document.uploadedAt).format("MM/DD/YYYY h:mm A")}</td>
      <td>
        <div className={styles.actions}>
          {isRecent ? (
            <span style={{ width: 21 }} />
          ) : (
            <a target="_blank" href={document.downloadUrl} title="Download" rel="noreferrer">
              <GetAppIcon fontSize="small" />
            </a>
          )}

          {document.isDeletable ? (
            <a
              className={styles.remove}
              onClick={() => setRemovingDocument(document)}
              title="Remove"
              data-testid={`delete_${document.name}_${document.id}`}
            >
              &times;
            </a>
          ) : (
            <span style={{ width: 21 }} />
          )}
        </div>
      </td>
    </tr>
  );
};

const Documents: React.FC<Props> = (props) => {
  const [removing, setRemovingDocument] = React.useState<UploadedDocument | null>(null);
  const [remove, removeQuery] = useMutation<
  RemoveUploadedDocument,
  RemoveUploadedDocumentVariables
  >(REMOVE_DOCUMENT);
  const { openSnackbar } = useSnackbar();

  const { query } = props;
  if (query.loading) return <em>Loading documents...</em>;

  if (query.error) {
    return <em>There was a problem retrieving the documents for this application.</em>;
  }

  if (!query.data || query.data.interest === null) return null;

  const {
    interest: { documents },
  } = query.data;

  if (documents.length === 0) {
    return (
      <div className={styles.empty}>
        <div>
          <InsertDriveFileIcon className={styles.emptyIcon} />
        </div>
        <div>There are no documents.</div>
        <div>
          Upload documents from your computer with the &quot;Choose Files&quot; button above.
        </div>
      </div>
    );
  }

  const sorted = orderBy(documents, ...SORT_BY_ARGS) as DocumentType[];
  return (
    <div>
      <table className={css("table", "table-condensed", styles.table)}>
        <thead>
          <tr>
            <th>Document type</th>
            <th>Filename</th>
            <th>Date uploaded</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((document, index) => (<DocumentRow
            key={`document-${document.id}`}
            document={document}
            index={index}
            documents={sorted}
            setRemovingDocument={setRemovingDocument}
          />))}
        </tbody>
      </table>

      <Modal
        title="Are you sure you want to delete this document?"
        isOpen={removing !== null}
        onRequestClose={() => setRemovingDocument(null)}
        style={{ minHeight: 0 }}
      >
        <DialogContent>
          You are about to delete <span className="label label-default">{removing?.name}</span>.
        </DialogContent>
        <DialogActions>
          <Button
            size="L"
            color="secondary"
            disabled={removeQuery.loading}
            onClick={() => setRemovingDocument(null)}
          >
            Cancel
          </Button>
          <Button
            size="L"
            color="dangerous"
            disabled={removeQuery.loading}
            onClick={async () => {
              if (!removing) return;
              const removed = await remove({
                variables: {
                  documentId: removing.id,
                  interestId: props.interestID,
                  type: removing.type,
                },
              });
              if (removed) {
                openSnackbar({ message: "Document Successfully Deleted", status: "success" });
              } else {
                openSnackbar({ message: "Error Removing Document", status: "error" });
              }
              await query.refetch();
              setRemovingDocument(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Modal>
    </div>
  );
};

export default Documents;
